import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { updateTokens } from "../../redux/features/auth/authSlice";
import {
  changeMyLocation,
  deleteMyLocation,
  getListMyLocation,
} from "../../services/profileApi";
import { FaCheck, FaPlus } from "react-icons/fa6";
import { IoArrowBack, IoTrashOutline } from "react-icons/io5";
import { PiPencil } from "react-icons/pi";
import SwipeConfirm from "../../components/swipeComp/SwipeConfirm";
import ConfirmModal from "../../components/modalComp/ConfirmModal";
import { useContext, useState } from "react";
import { useNotification } from "../../components/context/NotificationContext";
import HOC from "../../components/HOC";
import NotFound from "../../assets/notFound.png";
import { GlobalStateContext } from "../../components/context/GlobalStateContext";
import { useQueryClient } from "@tanstack/react-query";

const ListMyAddress = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const nearbyParam = searchParams.get("nearby");
  const isNearby = nearbyParam === "true";
  const navigate = useNavigate();
  const companyId = localStorage.getItem("companyId");
  const { showNotification } = useNotification();
  const { isLogin } = useContext(GlobalStateContext);

  const [openConfirm, setOpenConfirm] = useState(false);
  const [locationId, setLocationId] = useState(null);

  const {
    data: dataList,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["list-my-location"],
    queryFn: () => getListMyLocation(),
    enabled: !!isLogin,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });

  const { mutate: mutateDelete } = useMutation({
    mutationFn: deleteMyLocation,
    onSuccess: () => {
      setOpenConfirm(false);
      showNotification("Berhasil menghapus alamat", "success");
      refetch();
    },
    onError: () => {
      showNotification("Gagal menghapus alamat", "error");
      refetch();
    },
  });

  const { mutate: mutateChange } = useMutation({
    mutationFn: changeMyLocation,
    onSuccess: async (tokenData) => {
      await queryClient.invalidateQueries({
        queryKey: ["detail-list-outlet"],
      });

      await queryClient.invalidateQueries({
        queryKey: ["list-my-location"],
      });

      if (tokenData) {
        dispatch(
          updateTokens({
            accessToken: tokenData.access,
            accessExpired: tokenData.access_expired,
            renewToken: tokenData.renew,
            renewExpired: tokenData.renew_expired,
          }),
        );
      }
      showNotification("Berhasil mengubah alamat", "success");

      if (isNearby) {
        navigate("/location-address");
      } else {
        navigate("/location-address");
      }
    },
    onError: () => {
      showNotification("Gagal mengubah alamat", "error");
      refetch();
    },
  });

  return (
    <div className="w-full md:mt-4 min-h-screen">
      <div className="flex justify-center min-h-screen">
        <div className="w-full max-w-7xl mx-auto md:px-5 lg:px-0">
          <div className="hidden md:block mb-6 mt-4">
            <nav className="text-base text-slate-600 mb-3 flex flex-wrap items-center gap-1 font-medium">
              <button
                type="button"
                onClick={() => navigate("/")}
                className="hover:text-slate-900 hover:underline transition-colors"
              >
                Beranda
              </button>
              <span className="text-slate-400">/</span>
              <button
                type="button"
                onClick={() => navigate("/location-address")}
                className="hover:text-slate-900 hover:underline transition-colors"
              >
                Lokasi & Pengiriman
              </button>
              <span className="text-slate-400">/</span>
              <span className="text-primary">List Alamat</span>
            </nav>

            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <h1 className="text-2xl lg:text-3xl font-semibold text-slate-800 tracking-tight">
                  Alamat Saya
                </h1>

                <p className="text-slate-500 mt-1 text-sm lg:text-base">
                  Silahkan pilih alamat yang ingin digunakan atau tambahkan
                  alamat baru jika belum ada.
                </p>
              </div>

              <button
                type="button"
                onClick={() => navigate("/location-address")}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-md font-medium text-slate-700 shadow-sm hover:bg-secondaryActive hover:border-secondary/30 hover:text-secondary transition-colors"
              >
                <IoArrowBack size={18} />
                Kembali
              </button>
            </div>
          </div>

          <div className="rounded-md p-3 lg:p-0 w-full h-fit">
            <div className="hidden md:flex justify-end mb-5">
              <button
                onClick={() => navigate("/location-address/my-address/new")}
                className="h-[42px] border-2 border-secondary flex items-center gap-2 justify-center bg-secondary text-white px-5 tracking-wide font-medium rounded-xl hover:bg-secondaryHover transition"
              >
                <FaPlus size={12} />
                <p>Tambah Alamat Baru</p>
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-4">
              {isLoading ? (
                <>
                  {[1, 2, 3, 4].map((skeleton) => (
                    <div
                      key={skeleton}
                      className="flex flex-col p-3 rounded-xl border border-gray-200 bg-white animate-pulse"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-32 h-4 bg-gray-200 rounded-md" />
                        <div className="w-4 h-4 bg-gray-200 rounded-full" />
                      </div>

                      <div className="flex justify-between items-start gap-2">
                        <div className="flex-1 min-w-0 space-y-2">
                          <div className="w-40 h-3 bg-gray-200 rounded-md" />
                          <div className="w-full h-3 bg-gray-200 rounded-md" />
                          <div className="w-3/4 h-3 bg-gray-200 rounded-md" />
                        </div>

                        <div className="flex gap-1 self-end">
                          <div className="w-8 h-8 bg-gray-200 rounded-lg" />
                          <div className="w-8 h-8 bg-gray-200 rounded-lg" />
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              ) : dataList !== null ? (
                dataList?.map((item, index) => (
                  <div
                    key={index}
                    onClick={() => {
                      if (item.is_priority) return;

                      mutateChange({
                        location_id: item.location_id,
                        company_id: Number(companyId),
                        action_type: isNearby ? 1 : 0,
                      });
                    }}
                    className={`flex flex-col p-3 rounded-xl border cursor-pointer transition ${
                      item.is_priority
                        ? "border-secondary bg-secondaryActive md:border-primary"
                        : "border-gray-300 bg-white hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center gap-1 mb-1">
                      <h1 className="font-semibold text-slate-700 text-base">
                        {item.username}
                      </h1>

                      {item.is_priority && (
                        <FaCheck className="text-secondary" />
                      )}
                    </div>

                    <div className="flex justify-between items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-md text-primary font-medium mb-1">
                          {item.phone_number}
                        </p>

                        <p className="text-sm text-gray-500 break-words">
                          {item.address}
                        </p>
                      </div>

                      <div className="flex gap-1 self-end">
                        <div
                          onClick={(e) => {
                            e.stopPropagation();

                            navigate(
                              `/location-address/my-address/edit/${item.location_id}`,
                            );
                          }}
                          className="border border-gray-300 rounded-lg p-2 cursor-pointer hover:bg-gray-50"
                        >
                          <PiPencil size={18} className="text-gray-500" />
                        </div>

                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenConfirm(true);
                            setLocationId(item.location_id);
                          }}
                          className="border border-gray-300 rounded-lg p-2 cursor-pointer hover:bg-red-50"
                        >
                          <IoTrashOutline size={18} className="text-error" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="min-h-[calc(100vh-220px)] w-full col-span-full flex items-center justify-center">
                  <div className="flex flex-col items-center justify-center text-slate-700 px-4">
                    <img
                      src={NotFound}
                      alt="Not Found"
                      className="w-1/2 md:w-60 mb-3"
                    />

                    <h1 className="font-semibold text-lg text-center">
                      Belum ada alamat yang diset
                    </h1>

                    <p className="text-center text-md mt-2">
                      Silahkan tambahkan alamat baru terlebih dahulu.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="text-base fixed md:hidden bottom-0 left-0 right-0 bg-white rounded-t-3xl px-3 py-4 z-[99] max-h-[70vh] flex flex-col shadow-2xl">
        <button
          onClick={() => navigate("/location-address/my-address/new")}
          className="h-[40px] border-2 border-secondary flex items-center gap-2 justify-center bg-secondary text-white w-full tracking-wide font-medium rounded-lg focus:outline-none focus:shadow-outline hover:bg-secondaryHover transform duration-200 hover:scale-100 disabled:bg-slate-400 disabled:cursor-disabled"
        >
          <FaPlus size={12} />
          <p>Tambah Alamat Baru</p>
        </button>
      </div>

      <div className="md:hidden">
        <SwipeConfirm
          open={openConfirm}
          onClose={() => setOpenConfirm(false)}
          title={"Hapus Alamat?"}
          subTitle={"Hapus"}
          desc={
            "Anda yakin ingin menghapus alamat ini? Alamat ini akan dihapus secara permanen."
          }
          handleConfirm={() => {
            if (locationId !== null) {
              mutateDelete(locationId);
            }
          }}
        />
      </div>

      <div className="hidden md:block">
        <ConfirmModal
          open={openConfirm}
          setOpen={setOpenConfirm}
          title={"Hapus Alamat?"}
          subTitle={
            "Anda yakin ingin menghapus alamat ini? Alamat ini akan dihapus secara permanen."
          }
          confirmLabel="Hapus"
          handleConfirm={() => {
            if (locationId !== null) {
              mutateDelete(locationId);
            }
          }}
        />
      </div>
    </div>
  );
};

export default HOC(ListMyAddress, "required-auth");
